import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Select } from '@tempo-io/sui-select';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemStatusBadge } from '@/common/components/ui/itemStatusBadge';
import { useGetItemStatusesQuery } from '@/common/queries/useGetItemStatusesQuery';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';

type StatusOption = {
  value: number;
  label: string;
  color: string;
};

export const StatusEditCell = ({
  id,
  row,
  field,
  api,
}: GridRenderEditCellParams<ItemsTableRow>) => {
  const cellId = `status-${id}-${field}`;
  const { t } = useTranslation();
  const itemId = Number(id);

  const { data: itemStatuses } = useGetItemStatusesQuery();

  const statusOptions = React.useMemo<StatusOption[]>(
    () =>
      itemStatuses?.map((status) => ({
        value: status.id,
        label: status.name,
        color: status.color,
      })) ?? [],
    [itemStatuses],
  );

  const { commit, isPending } = useTableCellMutation();

  const selectedOption = React.useMemo(() => {
    if (!row.itemStatusId) return null;
    return statusOptions.find((opt) => opt.value === row.itemStatusId) ?? null;
  }, [row.itemStatusId, statusOptions]);

  return (
    <Select<StatusOption>
      id={cellId}
      name={cellId}
      cellEdit
      autoFocus
      openMenuOnFocus
      menuPlacement="auto"
      isClearable
      value={selectedOption}
      onChange={(opt) => {
        if (!opt) return;
        commit({
          itemId,
          api,
          row,
          data: { itemStatusId: opt.value },
        });
      }}
      options={statusOptions}
      formatOptionLabel={(option) => <ItemStatusBadge color={option.color} label={option.label} />}
      getOptionValue={(option) => String(option.value)}
      getOptionLabel={(option) => option.label}
      placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.status)}
      isDisabled={isPending}
      closeMenuOnScroll={(e: Event) =>
        e.target instanceof HTMLElement && !e.target.closest('[class*="-menu"]')
      }
    />
  );
};
